/**
 * SjImageStepper — modular image picker (strip + overflow menu).
 *
 * Internal San Jose (Ryan)
 * Used by: Ring Style · Band Style · Seal Design
 * layout: 'strip' shows 3–4 options at once; chevron lists the rest.
 * On phone widths the strip becomes a swipe scroller (all cards, no arrows).
 * layout: 'menu' — single trigger (image + name) + full dropdown (no strip).
 */
(function (global) {
  'use strict';

  const SVG_PREV =
    '<svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M12.5 4.5L7 10l5.5 5.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const SVG_NEXT =
    '<svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M7.5 4.5L13 10l-5.5 5.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const SVG_CHEVRON =
    '<svg width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true"><path d="M1.5 1.75L6 6.25L10.5 1.75" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function isSwipeUi() {
    try {
      return window.matchMedia('(max-width: 959px)').matches;
    } catch (err) {
      return false;
    }
  }

  var instances = [];
  var breakpointBound = false;
  function watchBreakpoint() {
    if (breakpointBound || typeof window === 'undefined' || !window.matchMedia) return;
    breakpointBound = true;
    var mq = window.matchMedia('(max-width: 959px)');
    var refresh = function () {
      instances.forEach(function (stepper) {
        if (!stepper || !stepper.root) return;
        stepper.applySwipeClass();
        stepper.syncFromValue(undefined, { keepWindow: true });
      });
    };
    if (mq.addEventListener) mq.addEventListener('change', refresh);
    else if (mq.addListener) mq.addListener(refresh);
  }

  function normalizeOptions(options) {
    return (options || [])
      .map((opt) => {
        if (!opt) return null;
        if (typeof opt === 'string') {
          return { value: opt, label: opt, imageUrl: '' };
        }
        const value = String(opt.value ?? opt.code ?? '').trim();
        if (!value) return null;
        return {
          value,
          label: String(opt.label ?? opt.name ?? value).trim() || value,
          menuLabel: String(opt.menuLabel ?? opt.menu_label ?? '').trim(),
          imageUrl: String(opt.imageUrl || opt.image || '').trim(),
        };
      })
      .filter(Boolean);
  }

  class SjImageStepper {
    /**
     * @param {object} cfg
     * @param {HTMLElement} cfg.mountEl
     * @param {Array} cfg.options
     * @param {() => string} cfg.getValue
     * @param {(value: string) => void} cfg.setValue
     * @param {string} [cfg.kind]
     * @param {string} [cfg.layout] 'strip' | 'menu' (default strip)
     * @param {number} [cfg.visibleCount] how many cards in the strip window (default 3)
     * @param {string} [cfg.emptyLabel]
     * @param {string} [cfg.ariaLabel]
     */
    constructor(cfg) {
      this.mountEl = cfg.mountEl;
      this.getValue = cfg.getValue;
      this.setValue = cfg.setValue;
      this.kind = cfg.kind || 'item';
      this.layout = cfg.layout || 'strip';
      this.visibleCount = Math.max(2, Number(cfg.visibleCount) || 3);
      this.emptyLabel = cfg.emptyLabel || 'Choose option';
      this.ariaLabel = cfg.ariaLabel || 'Options';
      this.options = normalizeOptions(cfg.options);
      this.windowStart = 0;
      this.open = false;
      this._swipeBound = false;
      this._onDocPointer = (e) => {
        if (!this.root || this.root.contains(e.target)) return;
        this.setOpen(false);
      };
      this._swipeHintPlayed = false;
      instances.push(this);
      watchBreakpoint();
      this.render();
      this.ensureSelectionInWindow();
      this.syncFromValue();
      this.playSwipeHintOnce();
    }

    destroy() {
      document.removeEventListener('pointerdown', this._onDocPointer, true);
      var i = instances.indexOf(this);
      if (i >= 0) instances.splice(i, 1);
      if (this.root) this.root.remove();
      this.root = null;
    }

    setOptions(options) {
      const prev = this.getValue();
      this.options = normalizeOptions(options);
      this.windowStart = 0;
      if (prev && this.options.some((o) => o.value === prev)) {
        this.ensureSelectionInWindow(prev);
        this.syncFromValue(prev);
      } else if (this.options.length) {
        this.selectValue(this.options[0].value, { silent: false });
      } else {
        this.syncFromValue('');
      }
    }

    indexOf(value) {
      return this.options.findIndex((o) => o.value === value);
    }

    visibleSlotCount() {
      return Math.min(this.visibleCount, Math.max(1, this.options.length));
    }

    isSwipeMode() {
      if (this.layout === 'menu') return false;
      if (this.kind === 'seal' && this.options.length <= this.visibleSlotCount()) {
        return false;
      }
      // Phone: ring + band (+ long seal lists) are a swipe strip.
      if (isSwipeUi()) return true;
      // Desktop: band only — fade the trailing icons and wheel-scroll the list.
      return this.kind === 'band' && this.options.length > this.visibleSlotCount();
    }

    applySwipeClass() {
      if (!this.root) return;
      this.root.classList.toggle('sj-image-stepper--swipe', this.isSwipeMode());
    }

    playSwipeHintOnce() {
      if (this._swipeHintPlayed || !this.root) return;
      if (this.kind !== 'band' || !this.isSwipeMode()) return;
      if (this.options.length <= this.visibleSlotCount()) return;
      try {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      } catch (_) {
        /* ignore */
      }
      this._swipeHintPlayed = true;
      const root = this.root;
      const start = () => {
        if (!root.isConnected) return;
        root.classList.add('is-swipe-hint');
        const stop = () => root.classList.remove('is-swipe-hint');
        root.addEventListener('pointerdown', stop, { once: true, passive: true });
        root.addEventListener('touchstart', stop, { once: true, passive: true });
        window.setTimeout(stop, 2400);
      };
      window.setTimeout(start, 500);
    }

    canSlide() {
      if (this.isSwipeMode()) return false;
      return this.options.length > this.visibleSlotCount();
    }

    normalizeWindowStart(start) {
      const len = this.options.length;
      if (!len) return 0;
      return ((start % len) + len) % len;
    }

    visibleOptions() {
      if (this.isSwipeMode()) return this.options.slice();
      const n = this.visibleSlotCount();
      const len = this.options.length;
      if (!len) return [];
      const out = [];
      for (let i = 0; i < n; i++) {
        out.push(this.options[(this.windowStart + i) % len]);
      }
      return out;
    }

    overflowOptions() {
      if (this.layout === 'menu') {
        // Menu layout lists every option in the dropdown.
        return this.options.slice();
      }
      if (this.isSwipeMode()) return [];
      const visible = new Set(this.visibleOptions().map((o) => o.value));
      return this.options.filter((o) => !visible.has(o.value));
    }

    ensureSelectionInWindow(forcedValue) {
      const value = forcedValue != null ? forcedValue : this.getValue();
      const i = this.indexOf(value);
      if (i < 0 || !this.options.length) return;
      if (!this.canSlide()) {
        this.windowStart = 0;
        return;
      }
      const visibleVals = new Set(this.visibleOptions().map((o) => o.value));
      if (!visibleVals.has(value)) {
        // Bring selection into view (as the first card); wrap stays infinite via stepWindow
        this.windowStart = this.normalizeWindowStart(i);
      }
    }

    selectValue(value, { silent } = {}) {
      const opt = this.options.find((o) => o.value === value);
      if (!opt) return;
      if (!silent) this.setValue(opt.value);
      // Pin window to the chosen band (browse arrows must NOT do this)
      this.syncFromValue(opt.value, { keepWindow: false });
      this.setOpen(false);
    }

    stepWindow(delta) {
      if (!this.canSlide()) return;
      this.windowStart = this.normalizeWindowStart(this.windowStart + delta);
      this.setOpen(false);
      // Keep browse position — do not snap back to the selected band
      this.syncFromValue(undefined, { keepWindow: true });
    }

    setOpen(next) {
      const overflow = this.overflowOptions();
      this.open = Boolean(next) && overflow.length > 0;
      if (!this.root) return;
      this.root.classList.toggle('is-open', this.open);
      const menu = this.root.querySelector('[data-sj-stepper-menu]');
      if (menu) {
        menu.hidden = !this.open;
        if (this.open) this.renderMenu();
      }
      const chevron = this.root.querySelector('[data-sj-stepper-chevron]');
      if (chevron) chevron.setAttribute('aria-expanded', this.open ? 'true' : 'false');
      const trigger = this.root.querySelector('[data-sj-stepper-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', this.open ? 'true' : 'false');
      if (this.open) {
        document.addEventListener('pointerdown', this._onDocPointer, true);
      } else {
        document.removeEventListener('pointerdown', this._onDocPointer, true);
      }
    }

    syncFromValue(forced, { keepWindow = false } = {}) {
      if (!this.root) return;
      this.applySwipeClass();
      const value = forced != null ? forced : this.getValue();
      if (!keepWindow) this.ensureSelectionInWindow(value);
      this.windowStart = this.normalizeWindowStart(this.windowStart);
      this.root.style.setProperty('--sj-stepper-visible', String(this.visibleSlotCount()));
      if (this.layout === 'menu') {
        this.renderTrigger();
      } else {
        this.renderStrip();
      }
      this.renderMenu();
      this.updateChrome();
    }

    updateChrome() {
      if (!this.root) return;
      const canSlide = this.canSlide();
      this.root.classList.toggle('is-single', this.options.length <= 1);
      this.root.classList.toggle('can-slide', canSlide);
      const prev = this.root.querySelector('[data-sj-stepper-prev]');
      const next = this.root.querySelector('[data-sj-stepper-next]');
      // Infinite wrap — arrows stay active whenever there's more than one window
      if (prev) prev.disabled = !canSlide;
      if (next) next.disabled = !canSlide;
      const chevron = this.root.querySelector('[data-sj-stepper-chevron]');
      const overflow = this.overflowOptions();
      if (this.layout === 'menu') {
        if (chevron) {
          chevron.hidden = false;
          chevron.disabled = this.options.length <= 1;
        }
        return;
      }
      if (chevron) {
        chevron.hidden = overflow.length === 0;
        chevron.disabled = overflow.length === 0;
      }
    }

    renderTrigger() {
      const trigger = this.root && this.root.querySelector('[data-sj-stepper-trigger]');
      if (!trigger) return;
      const selected = this.getValue();
      const opt =
        this.options.find((o) => o.value === selected) || this.options[0] || null;
      if (!opt) {
        trigger.innerHTML = `<span class="sj-image-stepper__trigger-label">${this.emptyLabel}</span>`;
        return;
      }
      trigger.innerHTML =
        (opt.imageUrl
          ? `<img class="sj-image-stepper__trigger-img" src="${opt.imageUrl}" alt="" loading="lazy">`
          : `<span class="sj-image-stepper__trigger-img sj-image-stepper__trigger-img--empty" aria-hidden="true"></span>`) +
        `<span class="sj-image-stepper__trigger-label">${opt.menuLabel || opt.label}</span>`;
    }

    renderStrip() {
      const track = this.root && this.root.querySelector('[data-sj-stepper-track]');
      if (!track) return;
      const selected = this.getValue();
      track.innerHTML = '';
      const visible = this.visibleOptions();
      if (!visible.length) {
        const empty = document.createElement('div');
        empty.className = 'sj-image-stepper__empty';
        empty.textContent = this.emptyLabel;
        track.appendChild(empty);
        return;
      }
      visible.forEach((opt) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sj-image-stepper__card';
        btn.setAttribute('data-sj-stepper-card', '');
        btn.setAttribute('data-value', opt.value);
        btn.classList.toggle('is-selected', opt.value === selected);
        btn.setAttribute('aria-pressed', opt.value === selected ? 'true' : 'false');
        btn.innerHTML =
          (opt.imageUrl
            ? `<img class="sj-image-stepper__card-img" src="${opt.imageUrl}" alt="" width="80" height="80" decoding="async">`
            : `<span class="sj-image-stepper__card-img sj-image-stepper__card-img--empty" aria-hidden="true"></span>`) +
          `<span class="sj-image-stepper__card-label">${opt.label}</span>`;
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.selectValue(opt.value);
        });
        track.appendChild(btn);
      });
      this.scrollSelectedIntoView();
    }

    scrollSelectedIntoView() {
      if (!this.isSwipeMode() || !this.root) return;
      const track = this.root.querySelector('[data-sj-stepper-track]');
      const selected = track && track.querySelector('.sj-image-stepper__card.is-selected');
      if (!track || !selected) return;
      const presetMatrix = Boolean(
        this.root.closest('.preset-3d-options[data-variant-matrix="true"]')
      );
      const align = () => {
        if (presetMatrix) {
          // Preset band: keep the strip left-aligned with size/metal chips.
          // Only nudge if the chosen card is clipped — never pin it to the
          // left (that scrolled the first band off-screen on every change).
          const cardLeft = selected.offsetLeft;
          const cardRight = cardLeft + selected.offsetWidth;
          const viewLeft = track.scrollLeft;
          const viewRight = viewLeft + track.clientWidth;
          let next = null;
          if (cardLeft < viewLeft + 2) {
            next = Math.max(0, cardLeft);
          } else if (cardRight > viewRight - 2) {
            next = Math.max(0, cardRight - track.clientWidth);
          }
          if (next == null) return;
          if (typeof track.scrollTo === 'function') {
            track.scrollTo({ left: next, behavior: 'smooth' });
          } else {
            track.scrollLeft = next;
          }
          return;
        }
        // Full builder: pin the chosen card to the left so the next tops
        // sit to the right (click-through when you can't swipe).
        const left = Math.max(0, selected.offsetLeft - 4);
        if (typeof track.scrollTo === 'function') {
          track.scrollTo({ left: left, behavior: 'smooth' });
        } else {
          track.scrollLeft = left;
        }
      };
      requestAnimationFrame(() => requestAnimationFrame(align));
    }

    bindSwipeGestures() {
      if (this._swipeBound || !this.root) return;
      const track = this.root.querySelector('[data-sj-stepper-track]');
      if (!track) return;
      this._swipeBound = true;
      let startX = 0;
      let startScroll = 0;
      let pointerId = null;
      let dragging = false;

      track.addEventListener('pointerdown', (e) => {
        if (!this.isSwipeMode()) return;
        startX = e.clientX;
        startScroll = track.scrollLeft;
        dragging = false;
        pointerId = e.pointerId;
      });
      track.addEventListener('pointermove', (e) => {
        if (!this.isSwipeMode() || pointerId !== e.pointerId) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 8) {
          if (!dragging && e.pointerType === 'mouse' && track.setPointerCapture) {
            try {
              track.setPointerCapture(e.pointerId);
            } catch (err) {
              /* ignore */
            }
          }
          dragging = true;
        }
        if (e.pointerType === 'mouse' && dragging) {
          track.scrollLeft = startScroll - dx;
        }
      });
      const endDrag = (e) => {
        if (pointerId !== e.pointerId) return;
        this._swipeDidDrag = dragging;
        dragging = false;
        pointerId = null;
      };
      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
      this._wheelAcc = 0;
      this._wheelLock = 0;
      const onWheel = (e) => {
        const dy = e.deltaY || 0;
        const dx = e.deltaX || 0;
        const menu = this.root.querySelector('[data-sj-stepper-menu]');
        const overMenu =
          this.open && menu && !menu.hidden && menu.contains(e.target);
        // Open overflow list: scroll the menu, never the strip or 3D viewer.
        if (overMenu || (this.open && menu && !menu.hidden)) {
          e.preventDefault();
          e.stopPropagation();
          if (overMenu) menu.scrollTop += dy;
          return;
        }
        if (this.isSwipeMode()) {
          if (Math.abs(dx) >= Math.abs(dy) && !dy) return;
          if (!dy && !dx) return;
          e.preventDefault();
          track.scrollLeft += dy || dx;
          return;
        }
        // Desktop windowed strip (ring style / seal): one notch = one card.
        if (!this.canSlide()) return;
        if (!dy && !dx) return;
        e.preventDefault();
        const now = Date.now();
        if (now - this._wheelLock < 90) return;
        this._wheelAcc += dy || dx;
        if (Math.abs(this._wheelAcc) < 28) return;
        this.stepWindow(this._wheelAcc > 0 ? 1 : -1);
        this._wheelAcc = 0;
        this._wheelLock = now;
      };
      this.root.addEventListener('wheel', onWheel, { passive: false });
      track.addEventListener(
        'click',
        (e) => {
          if (!this._swipeDidDrag) return;
          e.preventDefault();
          e.stopPropagation();
          this._swipeDidDrag = false;
        },
        true
      );
    }

    renderMenu() {
      const menu = this.root && this.root.querySelector('[data-sj-stepper-menu]');
      if (!menu) return;
      const selected = this.getValue();
      const overflow = this.overflowOptions();
      menu.innerHTML = '';
      if (!overflow.length) {
        if (this.layout !== 'menu') menu.hidden = true;
        return;
      }
      overflow.forEach((opt) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sj-image-stepper__option';
        btn.setAttribute('data-sj-stepper-option', '');
        btn.setAttribute('data-value', opt.value);
        btn.classList.toggle('is-selected', opt.value === selected);
        btn.innerHTML =
          (opt.imageUrl
            ? `<img class="sj-image-stepper__option-img" src="${opt.imageUrl}" alt="" loading="lazy">`
            : `<span class="sj-image-stepper__option-img sj-image-stepper__option-img--empty" aria-hidden="true"></span>`) +
          `<span class="sj-image-stepper__option-label">${opt.menuLabel || opt.label}</span>`;
        btn.addEventListener('click', () => this.selectValue(opt.value));
        menu.appendChild(btn);
      });
    }

    render() {
      if (this.root) this.root.remove();
      this._swipeBound = false;
      const root = document.createElement('div');
      root.className = `sj-image-stepper sj-image-stepper--${this.kind} sj-image-stepper--${this.layout}`;
      root.setAttribute('data-sj-image-stepper', this.kind);
      root.style.setProperty('--sj-stepper-visible', String(this.visibleCount));
      if (this.layout === 'menu') {
        root.innerHTML = `
          <button type="button" class="sj-image-stepper__trigger" data-sj-stepper-trigger aria-haspopup="listbox" aria-expanded="false" aria-label="${this.ariaLabel}"></button>
          <div class="sj-image-stepper__menu" data-sj-stepper-menu role="listbox" hidden></div>
        `;
        this.mountEl.appendChild(root);
        this.root = root;
        const trigger = root.querySelector('[data-sj-stepper-trigger]');
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.setOpen(!this.open);
        });
        // Alias for chrome helpers that look for a chevron
        trigger.setAttribute('data-sj-stepper-chevron', '');
        return;
      }
      root.innerHTML = `
        <div class="sj-image-stepper__row" role="group" aria-label="${this.ariaLabel}">
          <button type="button" class="sj-image-stepper__btn" data-sj-stepper-prev aria-label="Previous options">${SVG_PREV}</button>
          <div class="sj-image-stepper__viewport">
            <div class="sj-image-stepper__track" data-sj-stepper-track></div>
          </div>
          <button type="button" class="sj-image-stepper__btn" data-sj-stepper-next aria-label="Next options">${SVG_NEXT}</button>
        </div>
        <div class="sj-image-stepper__footer">
          <div class="sj-image-stepper__footer-extra" data-sj-stepper-footer-extra></div>
          <button type="button" class="sj-image-stepper__chevron" data-sj-stepper-chevron aria-haspopup="listbox" aria-expanded="false" aria-label="More options">${SVG_CHEVRON}</button>
        </div>
        <div class="sj-image-stepper__menu" data-sj-stepper-menu role="listbox" hidden></div>
      `;
      this.mountEl.appendChild(root);
      this.root = root;
      this.applySwipeClass();
      this.bindSwipeGestures();
      root.querySelector('[data-sj-stepper-prev]').addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.stepWindow(-1);
      });
      root.querySelector('[data-sj-stepper-next]').addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.stepWindow(1);
      });
      root.querySelector('[data-sj-stepper-chevron]').addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.setOpen(!this.open);
      });
    }

    /** Hook for callers (e.g. seal size chips) to render into the footer. */
    footerExtraEl() {
      return this.root ? this.root.querySelector('[data-sj-stepper-footer-extra]') : null;
    }
  }

  global.SjImageStepper = SjImageStepper;
})(typeof window !== 'undefined' ? window : globalThis);
